import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getPblOcCitizens } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCitizenProfile, Paginated } from '@/types/pbl';
import { formatPblEnglishChineseName } from '@/utils/pblDisplay';
import { errorHandler } from '@/utils/toastHandler';

import { TitleBar } from '../OcManageCases/styles';
import {
  CitizenPageShell,
  CitizenPanel,
  CitizenTable,
  CitizenTableScroll,
  EmptyText,
  InlineActions,
  SearchInput,
  Toolbar,
} from './styles';
import { useTranslation } from 'react-i18next';

const getCitizenName = (profile: PblCitizenProfile) =>
  formatPblEnglishChineseName(
    profile.citizen_full_name_en,
    profile.citizen_full_name_zh,
    profile.citizen_full_name || `Citizen #${profile.id}`
  );

const getCitizenEmail = (profile: PblCitizenProfile) => (profile.is_shadow_email ? '-' : profile.email || '-');

const buildCitizenDetailPath = (profileId: string | number) =>
  PATH.pbl.ocCitizenDetail.replace(':profileId', String(profileId));

const PblOcCitizenList = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageData, setPageData] = useState<Paginated<PblCitizenProfile> | null>(null);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    getPblOcCitizens({ keyword, per_page: 20, page: 1 })
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.ocCitizens.failedToLoadCitizenList'));
          return;
        }

        setPageData(response.data.data);
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocCitizens.failedToLoadCitizenList'));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [keyword, t]);

  const citizens = pageData?.data || [];

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.dashboard)}>
            {t('pbl.pagesHardcoded.ocCitizens.detail.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.ocCitizens.home')}<strong>{t('pbl.pagesHardcoded.ocCitizens.citizenList')}</strong>
          </span>
        </TitleBar>
      }
    >
      <CitizenPageShell>
        <CitizenPanel>
          <Toolbar>
            <SearchInput
              aria-label={t('pbl.pagesHardcoded.ocCitizens.searchCitizen')}
              placeholder={t('pbl.pagesHardcoded.ocCitizens.searchByNamePhoneOrEmail')}
              value={keywordDraft}
              onChange={(event) => setKeywordDraft(event.target.value)}
            />
            <InlineActions>
              <Button
                type="button"
                size="md"
                variant="primary"
                rounded={false}
                onClick={() => setKeyword(keywordDraft.trim())}
              >
                {t('pbl.pagesHardcoded.ocCitizens.search')}</Button>
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                onClick={() => {
                  setKeywordDraft('');
                  setKeyword('');
                }}
              >
                {t('pbl.pagesHardcoded.ocCitizens.clear')}</Button>
            </InlineActions>
          </Toolbar>

          {isLoading ? (
            <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.loadingCitizens')}</EmptyText>
          ) : citizens.length > 0 ? (
            <CitizenTableScroll>
              <CitizenTable>
                <thead>
                  <tr>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.citizens')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.hkid4Digits')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.phone')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.email')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.office')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.cases')}</th>
                    <th>{t('pbl.pagesHardcoded.ocCitizens.detail.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {citizens.map((profile) => (
                    <tr key={profile.id}>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.citizens')}>{getCitizenName(profile)}</td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.hkid4Digits')}>
                        {profile.identity_partial || '-'}
                      </td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.phone')}>{profile.phone || '-'}</td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.email')}>{getCitizenEmail(profile)}</td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.office')}>
                        {profile.oc_office_name || profile.oc_office?.name || profile.oc_office?.office_name || '-'}
                      </td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.cases')}>
                        {profile.cases_count ?? profile.cases?.length ?? 0}
                      </td>
                      <td data-label={t('pbl.pagesHardcoded.ocCitizens.detail.action')}>
                        <Button
                          type="button"
                          size="md"
                          variant="primary"
                          rounded={false}
                          onClick={() => navigate(buildCitizenDetailPath(profile.id))}
                        >
                          {t('pbl.pagesHardcoded.ocCitizens.detail.detail')}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CitizenTable>
            </CitizenTableScroll>
          ) : (
            <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.noCitizensFound')}</EmptyText>
          )}
        </CitizenPanel>
      </CitizenPageShell>
    </PageContainer>
  );
};

export default PblOcCitizenList;

